import React, { useEffect, useSyncExternalStore, useState } from "react";
import { Tooltip } from "@/components/common/Tooltip";

function getInitials(name?: string): string {
  return name?.trim()[0]?.toUpperCase() || "";
}

// Global photo version counter — all AvatarInitial instances subscribe to this
// so that every custom-photo avatar refreshes when any user's photo changes.
let photoVersion = 0;
const listeners = new Set<() => void>();

export function invalidatePhotoCache() {
  photoVersion++;
  listeners.forEach((l) => l());
}

function usePhotoVersion() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => photoVersion,
  );
}

function resolvePhotoUrl(
  photo: string,
  clientId?: string,
  version?: number,
): string {
  if (photo.startsWith("custom") && clientId) {
    const base = `/server/action/user/photo/${clientId}`;
    const v = photo.split(":")[1] || version;
    return v ? `${base}?v=${v}` : base;
  }
  return photo;
}

interface AvatarInitialProps {
  name: string;
  icon: string;
  color: string;
  clientId?: string;
  size?: "xs" | "sm" | "md" | "lg";
  tooltipTitle?: string;
}

export const AvatarInitial: React.FC<AvatarInitialProps> = ({
  name,
  icon,
  color,
  clientId,
  size = "sm",
  tooltipTitle,
}) => {
  const [hasError, setHasError] = useState(false);
  const version = usePhotoVersion();
  const resolvedIcon = icon?.trim()
    ? resolvePhotoUrl(icon.trim(), clientId, version)
    : "";
  const resolvedName = name ?? "";
  const showImage = resolvedIcon && !hasError;

  useEffect(() => {
    setHasError(false);
  }, [resolvedIcon]);

  const sizeClasses = {
    xs: "w-5 h-5",
    sm: "w-8 h-8",
    md: "w-11 h-11",
    lg: "w-[62px] h-[62px]",
  };

  const backgroundClass = color ? "" : "bg-bg-subtle";
  const fontSizes = {
    xs: "0.625rem",
    sm: "1rem",
    md: "1.375rem",
    lg: "1.9375rem",
  };
  const backgroundStyle = {
    ...(color ? { backgroundColor: color } : {}),
    fontSize: fontSizes[size],
  };
  const tooltipContent = tooltipTitle ?? resolvedName ?? "";

  const avatarContent = (
    <div
      className={`rounded-full flex items-center justify-center overflow-hidden ${!showImage ? backgroundClass : ""} ${sizeClasses[size]}`}
      style={backgroundStyle}
    >
      {showImage ? (
        <img
          src={resolvedIcon}
          alt={resolvedName}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setHasError(true)}
        />
      ) : (
        <span className="text-white font-normal">
          {getInitials(resolvedName)}
        </span>
      )}
    </div>
  );

  return tooltipContent ? (
    <Tooltip title={tooltipContent} placement="bottom">
      {avatarContent}
    </Tooltip>
  ) : (
    avatarContent
  );
};
